import { createSlice } from '@reduxjs/toolkit';

import type { AsyncStatus } from '@/shared/types/asyncState';

import type { PorteiroObservabilidadeResposta } from '../../types/porteiroObservabilidade';

import { fetchPorteiroObservabilidade } from './actions';

interface PorteiroObservabilidadeState {
  /** Mantido durante o recarregamento: trocar de filtro não apaga a tela e ela não pula. */
  data: PorteiroObservabilidadeResposta | null;
  status: AsyncStatus;
  error: string | null;
  /**
   * Falha da página seguinte fica separada de `error`: ela não pode trocar o painel pela moldura
   * de erro nem descartar as páginas que o admin já está lendo.
   */
  erroPaginacao: string | null;
  /**
   * Trocar de filtro dispara um fetch novo antes do anterior voltar. Sem esta guarda, a resposta
   * atrasada do recorte antigo sobrescreveria a do recorte escolhido.
   */
  requestIdAtual: string | null;
}

const initialState: PorteiroObservabilidadeState = {
  data: null,
  status: 'idle',
  error: null,
  erroPaginacao: null,
  requestIdAtual: null,
};

const porteiroObservabilidadeSlice = createSlice({
  name: 'porteiroObservabilidade',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPorteiroObservabilidade.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        state.erroPaginacao = null;
        state.requestIdAtual = action.meta.requestId;
      })
      .addCase(fetchPorteiroObservabilidade.fulfilled, (state, action) => {
        if (state.requestIdAtual !== action.meta.requestId) return;
        state.status = 'success';
        // Com cursor é a próxima página do mesmo recorte: resumo e opções não mudam, só os casos
        // se acumulam. Sem cursor é um recorte novo e substitui tudo.
        if (action.meta.arg.cursor && state.data) {
          state.data.casos.push(...action.payload.casos);
          state.data.proximoCursor = action.payload.proximoCursor;
          return;
        }
        state.data = action.payload;
      })
      .addCase(fetchPorteiroObservabilidade.rejected, (state, action) => {
        // Abortada é a requisição que a troca de filtro cancelou de propósito: não é erro.
        if (action.meta.aborted || state.requestIdAtual !== action.meta.requestId) return;
        if (action.meta.arg.cursor && state.data) {
          state.status = 'success';
          state.erroPaginacao = action.payload ?? 'Erro desconhecido';
          return;
        }
        state.status = 'error';
        state.error = action.payload ?? 'Erro desconhecido';
      });
  },
});

export const porteiroObservabilidadeReducer = porteiroObservabilidadeSlice.reducer;
